import { Injectable, computed, inject } from '@angular/core';
import type { ServiceTicketAssignee, TicketQueue } from '@common/types';
import { AccountAccessService } from './account-access.service';
import { AccountUsersService } from './account-users.service';
import { OrganizationService } from './organization.service';

/** Seed directory adapter. Production must return authorized account users and queue membership. */
@Injectable({ providedIn: 'root' })
export class TicketDirectoryService {
  private readonly users = inject(AccountUsersService);
  private readonly access = inject(AccountAccessService);
  private readonly organizations = inject(OrganizationService);

  readonly queues = computed(() => this.forOrganization(this.organizations.activeId() ?? ''));

  forOrganization(orgId: string): readonly TicketQueue[] {
    const accounts = this.access.accessForOrganization(orgId);
    return [
      {
        id: `${orgId}-central`,
        organizationId: orgId,
        accountIds: accounts.map((a) => a.id),
        name: 'Central complaints',
        memberIds: [...new Set(accounts.flatMap((a) => this.assignees(a.id).map((u) => u.id)))],
        defaultOwnerId: null,
      },
      ...accounts.map((a) => ({
        id: `${orgId}-${a.id}`,
        organizationId: orgId,
        accountIds: [a.id],
        name: `${a.name} team`,
        memberIds: this.assignees(a.id).map((u) => u.id),
        defaultOwnerId: null,
      })),
    ];
  }

  assignees(accountId: string): ServiceTicketAssignee[] {
    return this.users
      .all()
      .filter((u) => u.status === 'active' && u.accountIds?.includes(accountId))
      .map((u) => {
        const name = `${u.name} ${u.surname}`.trim() || u.email;
        return {
          id: u.id,
          name,
          initials: `${u.name[0] ?? ''}${u.surname[0] ?? ''}`.toUpperCase(),
        };
      });
  }

  forAccount(accountId: string): readonly TicketQueue[] {
    return this.queues().filter((q) => q.accountIds.includes(accountId));
  }
}
